
import { Button } from "@/components/ui/button";

interface PersonalGoalsContentProps {
  userPerformanceBand: any;
  onAccept: () => void;
  onDisagree: () => void;
  bandDisagreementStatus?: string;
  supervisorName?: string;
}

const PersonalGoalsContent = ({ 
  userPerformanceBand, 
  onAccept, 
  onDisagree, 
  bandDisagreementStatus,
  supervisorName 
}: PersonalGoalsContentProps) => {
  if (!userPerformanceBand || !userPerformanceBand.final_banding) {
    return (
      <div className="border border-gray-500/30 rounded-lg p-4">
        <div className="text-center">
          <p className="text-white text-sm">
            Your performance band has not been released. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  const status = userPerformanceBand.status;
  
  // Check if status is "Band disagreement resolution completed"
  if (status === 'Band disagreement resolution completed') {
    return (
      <div className="space-y-4">
        <div className="border border-primary rounded-lg p-4">
          <p className="text-white text-sm">
            You have been awarded a performance banding of {userPerformanceBand.final_banding}.
          </p>
        </div>
        
        <div className="border border-blue-500/30 rounded-lg p-3">
          <p className="text-blue-300 text-sm font-medium">
            Your band disagreement has been resolved with your supervisor. There will be no further changes to the performance band.
          </p>
        </div>
      </div>
    );
  }

  // Check if status is "Final Band Accepted"
  if (status === 'Final Band Accepted') {
    return (
      <div className="space-y-4">
        <div className="border border-primary rounded-lg p-4">
          <p className="text-white text-sm">
            You have been awarded a performance banding of {userPerformanceBand.final_banding}.
          </p>
        </div>
        
        <div className="border border-green-500/30 rounded-lg p-3">
          <p className="text-green-300 text-sm font-medium">
            The performance management process has been completed.
          </p>
        </div>
      </div>
    );
  }

  // Check if status is "Band disagreement submitted"
  if (status === 'Band disagreement submitted') {
    return (
      <div className="space-y-4">
        <div className="border border-primary rounded-lg p-4">
          <p className="text-white text-sm">
            You have been awarded a performance banding of {userPerformanceBand.final_banding}.
          </p>
        </div>
        
        <div className="border border-yellow-500/30 rounded-lg p-3">
          <p className="text-yellow-300 text-sm font-medium">
            The performance band disagreement has been raised and follow-up is ongoing. Please contact your supervisor.
          </p>
        </div>
      </div>
    );
  }

  // Check if band disagreement status is "submitted"
  if (bandDisagreementStatus === 'submitted') {
    return (
      <div className="space-y-4">
        <div className="border border-primary rounded-lg p-4">
          <p className="text-white text-sm">
            You have been awarded a performance banding of {userPerformanceBand.final_banding}.
          </p>
        </div>
        
        <div className="border border-yellow-500/30 rounded-lg p-3">
          <p className="text-yellow-300 text-sm font-medium">
            The performance band disagreement has been raised and follow-up is ongoing. Please contact your supervisor.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'Accepted') {
    return (
      <div className="space-y-4">
        <div className="border border-primary rounded-lg p-4">
          <p className="text-white text-sm">
            You have been awarded a performance banding of {userPerformanceBand.final_banding}.
          </p>
        </div>
        
        <div className="border border-green-500/30 rounded-lg p-3">
          <p className="text-green-300 text-sm font-medium">
            The performance management process has been completed.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'Band disagreement') {
    return (
      <div className="space-y-4">
        <div className="border border-primary rounded-lg p-4">
          <p className="text-white text-sm">
            You have been awarded a performance banding of {userPerformanceBand.final_banding}.
          </p>
        </div>
        
        <div className="border border-yellow-500/30 rounded-lg p-3">
          <p className="text-yellow-300 text-sm font-medium">
            The performance band disagreement has been raised and follow-up is ongoing. Please contact your supervisor.
          </p>
        </div>
      </div>
    );
  }

  // Default case - show accept/disagree buttons
  return (
    <div className="space-y-4">
      <div className="border border-primary rounded-lg p-4">
        <p className="text-white text-sm">
          You have been awarded a performance banding of {userPerformanceBand.final_banding}.
        </p>
      </div>
      
      <div className="border border-gray-500/30 rounded-lg p-3">
        <p className="text-white text-[12px] mb-3">
          If you click on Accept, this will be the end of the performance management cycle.
        </p>
        <p className="text-white text-[12px] mb-3">
          If you wish to raise a performance band disagreement, please click on the Disagree button for next steps.
        </p>
        
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            size="sm"
            className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30"
            onClick={onAccept}
          >
            Accept
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="bg-red-600/20 border-red-500 text-red-300 hover:bg-red-600/30"
            onClick={onDisagree}
          >
            Disagree
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PersonalGoalsContent;
